import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Send, MessageCircle, X, Minimize2, Maximize2, ChevronDown, ChevronLeft, Clock, Sparkles, Paperclip, Bookmark, Plus, Check, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ProposalCard, { ProposalRejectionWarning } from './components/ProposalCard';

// ── TableWrapper (copy-to-clipboard for markdown tables) ────────────────────
const TableWrapper = ({ children }) => {
  const tableRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!tableRef.current) return;
    const rows = tableRef.current.querySelectorAll('tr');
    const tsv = Array.from(rows)
      .map(row =>
        Array.from(row.querySelectorAll('th, td'))
          .map(cell => cell.textContent.trim())
          .join('\t')
      )
      .join('\n');
    navigator.clipboard.writeText(tsv).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard blocked (insecure context / permissions) — fail quietly so we
      // don't throw an unhandled rejection; the button just won't show "copied".
    });
  };

  return (
    <div className="relative overflow-x-auto my-3">
      <button
        onClick={handleCopy}
        className="absolute top-1 right-1 z-10 px-2 py-0.5 text-xs bg-surface-hover hover:bg-neutral-600 text-fg-muted hover:text-fg rounded border border-border-strong transition-colors"
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
      <table ref={tableRef} className="min-w-full bg-surface-raised border border-border rounded-lg text-sm">
        {children}
      </table>
    </div>
  );
};

const CoachMark = ({ className = '' }) => (
  <svg viewBox="0 0 32 32" fill="none" className={className} aria-hidden="true">
    <path d="M6 4H14L28 16L14 28H6L20 16L6 4Z" fill="currentColor" />
  </svg>
);

const CoachAvatar = ({ sidebar = false, className = '' }) => (
  <div
    className={`${sidebar ? 'w-5 h-5' : 'w-6 h-6'} bg-gradient-to-br from-primary-500 to-action rounded-lg flex items-center justify-center flex-shrink-0 border border-white/10 shadow-sm ${className}`}
  >
    <CoachMark className={`${sidebar ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-fg-on-action`} />
  </div>
);

// ── Markdown components for ReactMarkdown ───────────────────────────────────
const markdownComponents = {
  p:          ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
  strong:     ({children}) => <strong className="font-semibold">{children}</strong>,
  em:         ({children}) => <em className="italic">{children}</em>,
  ul:         ({children}) => <ul className="list-disc list-inside mb-2">{children}</ul>,
  ol:         ({children}) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
  li:         ({children}) => <li className="mb-1 break-words">{children}</li>,
  code:       ({children}) => <code className="bg-surface-hover text-fg px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
  blockquote: ({children}) => <blockquote className="border-l-4 border-border-strong pl-4 italic">{children}</blockquote>,
  table:  ({children}) => <TableWrapper>{children}</TableWrapper>,
  thead:  ({children}) => <thead className="bg-surface-subtle">{children}</thead>,
  tbody:  ({children}) => <tbody className="divide-y divide-border">{children}</tbody>,
  tr:     ({children}) => <tr className="hover:bg-surface-hover transition-colors">{children}</tr>,
  th:     ({children}) => <th className="px-3 py-2 text-left text-xs font-semibold text-fg-muted uppercase tracking-wider border-b border-border">{children}</th>,
  td:     ({children}) => <td className="px-3 py-2 text-sm text-fg-secondary border-b border-border">{children}</td>,
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatSessionDate = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)  return d.toLocaleDateString('en-GB', { weekday: 'long' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: diffDays > 365 ? 'numeric' : undefined });
};

// ── Collapsible section support ───────────────────────────────────────────────
// Splits message content by ## headings into sections.
function splitBySections(content) {
  const lines = content.split('\n');
  const sections = [];
  let current = { heading: null, lines: [] };
  for (const line of lines) {
    const match = line.match(/^## (.+)$/);
    if (match) {
      if (current.heading !== null || current.lines.join('').trim()) {
        sections.push({ heading: current.heading, body: current.lines.join('\n').trim() });
      }
      current = { heading: match[1].trim(), lines: [] };
    } else {
      current.lines.push(line);
    }
  }
  if (current.heading !== null || current.lines.join('').trim()) {
    sections.push({ heading: current.heading, body: current.lines.join('\n').trim() });
  }
  return sections;
}

const CollapsibleContent = ({ sections }) => {
  const firstHeadedIdx = sections.findIndex(s => s.heading !== null);
  const [openSet, setOpenSet] = useState(() =>
    new Set(firstHeadedIdx >= 0 ? [firstHeadedIdx] : [])
  );
  const toggle = useCallback((i) => {
    setOpenSet(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  }, []);
  return (
    <div className="space-y-1.5">
      {sections.map((section, i) => {
        if (section.heading === null) {
          return section.body ? (
            <div key={i}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {section.body}
              </ReactMarkdown>
            </div>
          ) : null;
        }
        const isOpen = openSet.has(i);
        return (
          <div key={i} className="border border-border-strong rounded-lg overflow-hidden">
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-center justify-between px-3 py-2 bg-surface-hover/60 hover:bg-surface-hover text-left transition-colors"
            >
              <span className="font-semibold text-fg text-sm leading-snug">{section.heading}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-fg-muted flex-shrink-0 ml-2 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && section.body && (
              <div className="px-3 py-2 border-t border-border">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {section.body}
                </ReactMarkdown>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Renders bot message content — uses collapsible sections when 2+ ## headings present.
// During streaming, renders plain markdown with a blinking cursor instead of
// the collapsible-section layout (which would be expensive to recompute on every chunk).
const BotMessageContent = ({ content, streaming }) => {
  const cleaned = content.replace(/<br\s*\/?>/gi, '\n');

  if (streaming) {
    return (
      <>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {cleaned}
        </ReactMarkdown>
        <span className="inline-block w-1.5 h-4 bg-primary-400 rounded-sm ml-0.5 align-middle animate-pulse" />
      </>
    );
  }

  const sections = splitBySections(cleaned);
  const headedCount = sections.filter(s => s.heading !== null).length;
  if (headedCount < 2) {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {cleaned}
      </ReactMarkdown>
    );
  }
  return <CollapsibleContent sections={sections} />;
};

/**
 * ChatPanel — the AI coach chat interface.
 *
 * Props:
 *   messages           – array of { id, type, content, timestamp }
 *   inputMessage       – current text in the input
 *   setInputMessage    – setter for inputMessage
 *   handleSendMessage  – send handler
 *   handleKeyPress     – keyboard handler (Enter)
 *   isTyping           – whether the bot is typing
 *   messagesEndRef     – ref for auto-scroll
 *   startNewConversation – handler to reset the chat
 *   profileCompletion  – { isActive, currentStep }
 *   profileQuestions   – array of { key, quickAnswers[] }
 *   handleQuickAnswer  – handler for profile quick-answer buttons
 *   sidebar            – if true, renders in compact sidebar mode
 *   expanded           – if true, sidebar is in wide mode
 *   onToggleExpand     – toggles sidebar width
 *   onClose            – called when the user collapses / closes the panel
 *   sessionHistory     – array of past session objects { id, date, preview, messageCount, messages }
 */
const ChatPanel = ({
  messages,
  inputMessage,
  setInputMessage,
  handleSendMessage,
  handleKeyPress,
  isTyping,
  messagesEndRef,
  startNewConversation,
  profileCompletion,
  profileQuestions,
  handleQuickAnswer,
  quickActions = [],
  onQuickAction,
  sidebar = false,
  expanded = false,
  onToggleExpand,
  onClose,
  sessionHistory = [],
  // Activity attachment
  recentActivities = [],
  attachedActivityIds = [],
  onAttachedActivityIdsChange,
  // Saved questions
  savedQuestions = [],
  onSavedQuestionsChange,
  // Memory check-in resolution: action ∈ 'confirm' | 'drop' | 'skip'
  onResolveCheckin,
  // Coach action proposals (Phase 1a): user confirms/discards via ProposalCard.
  // Signature: onConfirmProposal(messageId, proposal); onDiscardProposal(messageId, proposalId)
  // onConfirmAllProposals(messageId, proposals) — confirms all pending in one click.
  onConfirmProposal,
  onDiscardProposal,
  onConfirmAllProposals,
  // Controlled history view (used when ChatOverlay owns the state)
  historyView: controlledHistoryView,
  onHistoryViewChange,
}) => {
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [dismissedCards, setDismissedCards] = useState(new Set());
  const dismissCard = useCallback((id) => setDismissedCards(prev => new Set([...prev, id])), []);
  const [activeMenu, setActiveMenu] = useState(null); // null | 'suggestions' | 'activities' | 'saved'
  const [activityFilter, setActivityFilter] = useState('');
  // historyView: null = chat, 'list' = history list, object = viewing a specific session
  // Supports both controlled (from ChatOverlay) and uncontrolled (internal) modes
  const [internalHistoryView, setInternalHistoryView] = useState(null);
  const historyView = controlledHistoryView !== undefined ? controlledHistoryView : internalHistoryView;
  const setHistoryView = onHistoryViewChange || setInternalHistoryView;
  const scrollContainerRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-resize textarea as content grows (up to ~6 lines)
  const autoResize = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const maxH = sidebar ? 96 : 144; // ~4 lines sidebar, ~6 lines full
    ta.style.height = `${Math.min(ta.scrollHeight, maxH)}px`;
  }, [sidebar]);

  // Reset textarea height when input is cleared (after send)
  useEffect(() => {
    if (!inputMessage && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputMessage]);

  // Close menus on outside click or Escape
  const menuRef = useRef(null);
  useEffect(() => {
    if (!activeMenu) return;
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setActiveMenu(null); };
    const handleKey = (e) => { if (e.key === 'Escape') setActiveMenu(null); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [activeMenu]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 80);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Close history view if a new session is started externally
  useEffect(() => {
    if (historyView) setHistoryView(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages[0]?.id]);

  const scrollToLatest = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll as streaming content grows, unless the user has scrolled up
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg?.streaming) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // ── Header actions shared between sidebar and overlay headers ──────────────
  const headerActions = (
    <div className="flex items-center gap-1">
      {sessionHistory.length > 0 && (
        <button
          onClick={() => setHistoryView(v => v ? null : 'list')}
          className={`p-1 rounded transition-colors ${historyView ? 'text-action-link' : 'text-fg-muted hover:text-fg'}`}
          title="Conversation history"
        >
          <Clock className="w-4 h-4" />
        </button>
      )}
      {messages.length > 1 && !historyView && (
        <button
          onClick={startNewConversation}
          title="New conversation"
          className="text-xs text-fg-muted hover:text-fg px-2 py-1 rounded transition-colors"
        >
          New
        </button>
      )}
      {onToggleExpand && (
        <button onClick={onToggleExpand} className="p-1 text-fg-muted hover:text-fg rounded transition-colors" title={expanded ? 'Shrink chat' : 'Expand chat'}>
          {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      )}
      {onClose && (
        <button onClick={onClose} className="p-1 text-fg-muted hover:text-fg rounded transition-colors" title="Close chat">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  // ── History list panel ─────────────────────────────────────────────────────
  const historyListPanel = (
    <div className="flex-1 overflow-y-auto min-h-0 bg-surface">
      <p className="px-4 pt-4 pb-2 text-xs text-fg-subtle uppercase tracking-wider font-medium">
        Past sessions
      </p>
      {sessionHistory.map((session) => (
        <button
          key={session.id}
          onClick={() => setHistoryView(session)}
          className="w-full text-left px-4 py-3 hover:bg-surface-raised transition-colors border-b border-border-subtle last:border-0"
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-action-link">
              {formatSessionDate(session.date)}
            </span>
            <span className="text-xs text-fg-disabled">
              {session.messageCount} messages
            </span>
          </div>
          <p className="text-sm text-fg-secondary truncate leading-snug">
            {session.preview}
          </p>
          <p className="text-xs text-fg-disabled mt-0.5">
            {new Date(session.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </button>
      ))}
    </div>
  );

  // ── Session detail panel ───────────────────────────────────────────────────
  const sessionDetailPanel = (session) => (
    <div className="flex-1 overflow-y-auto min-h-0 bg-surface">
      <button
        onClick={() => setHistoryView('list')}
        className="flex items-center gap-1 px-4 py-3 text-xs text-fg-muted hover:text-fg transition-colors sticky top-0 bg-surface border-b border-border-subtle w-full"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Back to history
      </button>
      <div className="px-3 py-3 space-y-3">
        {session.messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`px-3 py-2 overflow-hidden ${
                message.type === 'user'
                  ? 'max-w-[85%] bg-gradient-to-br from-primary-500/70 to-action/70 text-fg-on-action rounded-2xl rounded-tr-md'
                  : 'w-full bg-surface-raised/70 text-fg-secondary border border-border rounded-2xl rounded-tl-md'
              }`}
            >
                <div className="flex items-start space-x-2">
                  {message.type === 'bot' && (
                    <CoachAvatar sidebar className="mt-0.5" />
                  )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] leading-relaxed break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {message.type === 'bot' ? (
                      <BotMessageContent content={message.content} />
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {message.content.replace(/<br\s*\/?>/gi, '\n')}
                      </ReactMarkdown>
                    )}
                  </div>
                  <p className="text-[10px] mt-1 text-fg-subtle">{message.timestamp}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className={`flex flex-col min-h-0 ${sidebar ? 'flex-1 min-w-0 overflow-hidden' : 'flex-1'}`}>

      {/* ── Header: sidebar panel (has close btn), or full-page chat (no close btn).
              Suppressed inside ChatOverlay (sidebar=true, onClose=undefined) since
              the overlay renders its own header with the clock button. */}
      {(sidebar ? !!onClose : (sessionHistory.length > 0 || messages.length > 1)) && (
        <div className="flex items-center justify-between px-3 py-2 bg-surface-raised border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-action-link" />
            <span className="text-sm font-medium text-fg">
              {historyView ? 'History' : 'AI Coach'}
            </span>
          </div>
          {headerActions}
        </div>
      )}

      {/* ── History views ──────────────────────────────────────────────────── */}
      {historyView === 'list' && historyListPanel}
      {historyView && historyView !== 'list' && sessionDetailPanel(historyView)}

      {/* ── Normal chat (hidden when history is open) ─────────────────────── */}
      {!historyView && (
        <>
          <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto min-h-0 space-y-3 bg-surface ${sidebar ? 'px-3 py-3' : 'px-4 py-5 space-y-4'}`} style={{ WebkitOverflowScrolling: 'touch' }}>
            {messages.map((message) => {
              // Dismissed proactive cards are hidden without removing them from state
              if (dismissedCards.has(message.id)) return null;

              // ── ACWR alert card ──────────────────────────────────────────
              if (message.type === 'alert') {
                const isHighRisk = message.level === 'high-risk';
                const bgCls = isHighRisk
                  ? 'bg-feedback-danger-bg/30 border-feedback-danger-border/60'
                  : message.level === 'undertraining'
                  ? 'bg-feedback-info-bg/30 border-feedback-info-border/60'
                  : 'bg-feedback-warning-bg/30 border-feedback-warning-border/60';
                const labelCls = isHighRisk ? 'text-feedback-danger-icon' : message.level === 'undertraining' ? 'text-feedback-info-text' : 'text-feedback-warning-icon';
                const icon = isHighRisk ? '🚨' : message.level === 'undertraining' ? '📉' : '⚠️';
                const label = isHighRisk ? 'High risk' : message.level === 'undertraining' ? 'Low load' : 'Training load alert';
                return (
                  <div key={message.id} className="flex justify-start">
                    <div className={`${sidebar ? 'px-3 py-2' : 'px-4 py-3'} w-full border rounded-2xl rounded-tl-md ${bgCls}`}>
                      <div className="flex items-start space-x-2">
                        <span className="text-base mt-0.5 flex-shrink-0">{icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className={`text-[11px] uppercase tracking-wide font-medium ${labelCls}`}>{label}</p>
                            <button onClick={() => dismissCard(message.id)} title="Dismiss" className="ml-2 text-fg-muted hover:text-fg transition-colors flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                          </div>
                          <p className={`${sidebar ? 'text-[13px]' : 'text-[14px]'} text-fg leading-relaxed mb-2`}>{message.content}</p>
                          {message.suggestion && (
                            <button
                              onClick={() => setInputMessage(message.suggestion)}
                              className={`${sidebar ? 'text-[11px] px-2.5 py-1' : 'text-xs px-3 py-1.5'} rounded-full border border-primary-500/50 text-primary-300 hover:bg-primary-500/10 transition-colors`}
                            >{message.suggestion}</button>
                          )}
                          <p className="text-[10px] mt-2 text-fg-subtle">{message.timestamp}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // ── Discipline nudge card ─────────────────────────────────────
              if (message.type === 'nudge') {
                const disciplineEmoji = { swim: '🏊', bike: '🚴', run: '🏃' }[message.discipline] || '🏋️';
                return (
                  <div key={message.id} className="flex justify-start">
                    <div className={`${sidebar ? 'px-3 py-2' : 'px-4 py-3'} w-full bg-surface-hover border border-border-strong rounded-2xl rounded-tl-md`}>
                      <div className="flex items-start space-x-2">
                        <span className="text-base mt-0.5 flex-shrink-0">{disciplineEmoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] uppercase tracking-wide text-fg-subtle">Discipline check</p>
                            <button onClick={() => dismissCard(message.id)} title="Dismiss" className="ml-2 text-fg-muted hover:text-fg transition-colors flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                          </div>
                          <p className={`${sidebar ? 'text-[13px]' : 'text-[14px]'} text-fg leading-relaxed mb-2`}>{message.content}</p>
                          {message.suggestion && (
                            <button
                              onClick={() => setInputMessage(message.suggestion)}
                              className={`${sidebar ? 'text-[11px] px-2.5 py-1' : 'text-xs px-3 py-1.5'} rounded-full border border-primary-500/50 text-primary-300 hover:bg-primary-500/10 transition-colors`}
                            >{message.suggestion}</button>
                          )}
                          <p className="text-[10px] mt-2 text-fg-subtle">{message.timestamp}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // ── Race-prep card ────────────────────────────────────────────
              if (message.type === 'race-prep') {
                return (
                  <div key={message.id} className="flex justify-start">
                    <div className={`${sidebar ? 'px-3 py-2' : 'px-4 py-3'} w-full bg-feedback-warning-bg/20 border border-feedback-warning-border/50 rounded-2xl rounded-tl-md`}>
                      <div className="flex items-start space-x-2">
                        <span className="text-base mt-0.5 flex-shrink-0">🏁</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] uppercase tracking-wide text-feedback-warning-icon font-medium">
                              {message.raceName} — {message.daysUntil} day{message.daysUntil === 1 ? '' : 's'} to go
                            </p>
                            <button onClick={() => dismissCard(message.id)} title="Dismiss" className="ml-2 text-fg-muted hover:text-fg transition-colors flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className={`${sidebar ? 'text-[13px]' : 'text-[14px]'} text-fg leading-relaxed mb-2 whitespace-pre-line`}>{message.content}</div>
                          {message.suggestion && (
                            <button
                              onClick={() => setInputMessage(message.suggestion)}
                              className={`${sidebar ? 'text-[11px] px-2.5 py-1' : 'text-xs px-3 py-1.5'} rounded-full border border-primary-500/50 text-primary-300 hover:bg-primary-500/10 transition-colors`}
                            >{message.suggestion}</button>
                          )}
                          <p className="text-[10px] mt-2 text-fg-subtle">{message.timestamp}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              if (message.type === 'checkin') {
                return (
                  <div key={message.id} className="flex justify-start">
                    <div className={`${sidebar ? 'px-3 py-2' : 'px-4 py-3'} w-full bg-feedback-info-bg/30 border border-feedback-info-border/60 rounded-2xl rounded-tl-md`}>
                      <div className="flex items-start space-x-2">
                        <CoachAvatar sidebar={sidebar} className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] uppercase tracking-wide text-fg-subtle mb-1">Memory check-in</p>
                          <p className={`${sidebar ? 'text-[13px]' : 'text-[14px]'} text-fg leading-relaxed mb-2`}>
                            Quick check-in — is this still true?
                          </p>
                          <p className={`${sidebar ? 'text-[12px]' : 'text-[13px]'} italic text-fg-secondary mb-3 break-words`} style={{ overflowWrap: 'anywhere' }}>
                            “{message.entryText}”
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => onResolveCheckin && onResolveCheckin(message.entryId, 'confirm')}
                              className="px-3 py-1.5 text-xs rounded-md bg-action text-fg-on-action hover:bg-action-hover transition-colors"
                            >Still true</button>
                            <button
                              onClick={() => onResolveCheckin && onResolveCheckin(message.entryId, 'drop')}
                              className="px-3 py-1.5 text-xs rounded-md bg-surface-hover text-fg-secondary border border-border-strong hover:bg-neutral-600 transition-colors"
                            >No, drop it</button>
                            <button
                              onClick={() => onResolveCheckin && onResolveCheckin(message.entryId, 'skip')}
                              className="px-3 py-1.5 text-xs rounded-md text-fg-muted hover:text-fg transition-colors"
                            >Skip</button>
                          </div>
                          <p className="text-[10px] mt-2 text-fg-subtle">{message.timestamp}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`${sidebar ? 'px-3 py-2' : 'px-4 py-3'} overflow-hidden ${
                    message.type === 'user'
                      ? `${sidebar ? 'max-w-[85%]' : 'max-w-xs lg:max-w-md'} bg-gradient-to-br from-primary-500 to-action text-fg-on-action rounded-2xl rounded-tr-md shadow-md`
                      : 'w-full bg-surface-raised text-fg border border-border rounded-2xl rounded-tl-md'
                  }`}
                >
                  <div className="flex items-start space-x-2">
                    {message.type === 'bot' && (
                      <CoachAvatar sidebar={sidebar} className="mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`${sidebar ? 'text-[13px]' : 'text-[15px]'} leading-relaxed break-words overflow-wrap-anywhere`}
                        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {message.type === 'bot' ? (
                          <BotMessageContent content={message.content} streaming={message.streaming} />
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {message.content.replace(/<br\s*\/?>/gi, '\n')}
                          </ReactMarkdown>
                        )}
                      </div>
                      <p className={`${sidebar ? 'text-[10px]' : 'text-xs'} mt-1 ${
                        message.type === 'user' ? 'text-feedback-info-text' : 'text-fg-muted'
                      }`}>
                        {message.timestamp}
                      </p>
                      {/* Suggestion chips — opener suggestions or request_clarification
                          answer options; tap fills the composer (does not auto-send). */}
                      {message.suggestions?.length > 0 && (
                        <div className={`flex flex-wrap gap-1.5 ${sidebar ? 'mt-2' : 'mt-3'}`}>
                          {message.suggestions.map((s, i) => (
                            <button
                              key={i}
                              onClick={() => setInputMessage(s)}
                              className={`${sidebar ? 'text-[11px] px-2.5 py-1' : 'text-xs px-3 py-1.5'} rounded-full border border-primary-500/50 text-primary-300 hover:bg-primary-500/10 hover:border-primary-400 transition-colors text-left leading-snug`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Coach action proposals (Phase 1a) — appear below prose
                          once the streaming response has been parsed. The raw
                          coach-actions block is stripped from `message.content`
                          before display; only the structured proposals survive. */}
                      {message.type === 'bot' && (message.rejectedCount > 0 || message.parseError) && (
                        <ProposalRejectionWarning count={message.rejectedCount} parseError={message.parseError} />
                      )}
                      {message.type === 'bot' && message.proposals?.length > 0 && (
                        <div className="space-y-1.5">
                          {message.proposals.filter(p => p.status === 'pending').length >= 2 && (
                            <div className="flex justify-end">
                              <button
                                onClick={() => onConfirmAllProposals && onConfirmAllProposals(message.id, message.proposals)}
                                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors"
                              >
                                ✓ Confirm all ({message.proposals.filter(p => p.status === 'pending').length})
                              </button>
                            </div>
                          )}
                          {message.proposals.map((p) => (
                            <ProposalCard
                              key={p.proposalId}
                              proposal={p}
                              compact={sidebar}
                              onConfirm={() => onConfirmProposal && onConfirmProposal(message.id, p)}
                              onDiscard={() => onDiscardProposal && onDiscardProposal(message.id, p)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}

            {/* Quick Answer Buttons for Profile Completion */}
            {profileCompletion?.isActive && profileCompletion.currentStep < (profileQuestions?.length || 0) && (
              <div className="flex justify-start">
                <div className={`bg-feedback-info-bg/40 border border-feedback-info-border rounded-lg ${sidebar ? 'p-3' : 'p-4'} max-w-lg w-full`}>
                  <div className="flex items-start space-x-2">
                    <CoachAvatar sidebar={sidebar} className="mt-0.5" />
                    <div className="flex-1">
                      <p className={`${sidebar ? 'text-[13px]' : 'text-[15px]'} text-feedback-info-text mb-2 font-medium`}>
                        Quick answers for: {profileQuestions[profileCompletion.currentStep].key}
                      </p>
                      <div className="grid gap-1.5">
                        {profileQuestions[profileCompletion.currentStep].quickAnswers?.map((answer, index) => (
                          <button
                            key={index}
                            onClick={() => handleQuickAnswer(answer)}
                            className={`text-left px-2 py-1.5 bg-surface-hover hover:bg-neutral-600 border border-border-strong rounded-md ${sidebar ? 'text-[13px]' : 'text-[15px]'} transition-colors text-fg-secondary hover:text-fg`}
                          >
                            {answer}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className={`bg-surface-raised border border-border ${sidebar ? 'px-3 py-2' : 'px-4 py-3'} rounded-2xl rounded-tl-md`}>
                  <div className="flex items-center space-x-2">
                    <CoachAvatar sidebar={sidebar} />
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 bg-primary-300 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-primary-300 rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
                      <div className="w-1.5 h-1.5 bg-primary-300 rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />

            {/* Scroll-to-latest button */}
            {showScrollBtn && (
              <button
                onClick={scrollToLatest}
                className="sticky bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 bg-surface-hover hover:bg-neutral-600 border border-neutral-500 text-fg text-xs rounded-full shadow-lg transition-colors z-10"
                title="Scroll to latest"
              >
                <ChevronDown className="w-3.5 h-3.5" />
                Latest
              </button>
            )}
          </div>

          {/* ── Input ─────────────────────────────────────────────────────────── */}
          <div className={`bg-surface-subtle border-t border-border flex-shrink-0 ${sidebar ? 'px-3 py-2' : 'px-4 pt-3 pb-4'}`}>
            {/* Attached activities chips */}
            {attachedActivityIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {attachedActivityIds.map(id => {
                  const act = recentActivities.find(a => a.id === id);
                  if (!act) return null;
                  const label = `${act.type?.charAt(0).toUpperCase() || ''}${act.type?.slice(1) || ''}: ${act.name || act.date?.slice(0, 10) || 'Activity'}`;
                  return (
                    <span key={id} className="inline-flex items-center gap-1 bg-feedback-info-bg/50 border border-feedback-info-border text-feedback-info-text text-[10px] px-2 py-0.5 rounded-full">
                      <span className="truncate max-w-[120px]">{label}</span>
                      <button onClick={() => onAttachedActivityIdsChange(attachedActivityIds.filter(x => x !== id))} className="hover:text-fg"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  );
                })}
              </div>
            )}

            <div className={`flex ${sidebar ? 'items-end space-x-2' : 'flex-col gap-2 sm:flex-row sm:items-end sm:gap-2'}`} ref={menuRef}>
              {/* Unified action menu */}
              <div className={`relative flex-shrink-0 ${sidebar ? 'self-end' : 'sm:self-end'}`}>
                {/* Menu popups */}
                {activeMenu === 'suggestions' && quickActions.length > 0 && (
                  <div className="absolute bottom-full left-0 mb-2 w-60 bg-surface-raised border border-border-strong rounded-xl shadow-xl overflow-hidden z-10">
                    {quickActions.map((action, i) => (
                      <button
                        key={i}
                        onClick={() => { onQuickAction(action.action); setActiveMenu(null); }}
                        className="flex items-center gap-2.5 w-full px-3 py-2.5 text-left hover:bg-surface-hover transition-colors border-b border-border last:border-0"
                      >
                        <action.icon className="w-3.5 h-3.5 text-action-link flex-shrink-0" />
                        <span className="text-xs text-fg-secondary">{action.text}</span>
                      </button>
                    ))}
                  </div>
                )}

                {activeMenu === 'activities' && (
                  <div className="absolute bottom-full left-0 mb-2 w-72 bg-surface-raised border border-border-strong rounded-xl shadow-xl overflow-hidden z-10">
                    <div className="px-3 py-2 border-b border-border">
                      <input
                        type="text"
                        value={activityFilter}
                        onChange={e => setActivityFilter(e.target.value)}
                        placeholder="Filter activities..."
                        className="w-full bg-surface-hover text-fg text-xs rounded-lg px-2 py-1.5 placeholder:text-fg-subtle focus:outline-none focus:ring-1 focus:ring-border-focus"
                      />
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {recentActivities
                        .slice()
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .filter(a => {
                          if (!activityFilter) return true;
                          const q = activityFilter.toLowerCase();
                          return (a.name || '').toLowerCase().includes(q) || (a.type || '').toLowerCase().includes(q);
                        })
                        .slice(0, 30)
                        .map(act => {
                          const isAttached = attachedActivityIds.includes(act.id);
                          const date = act.date ? new Date(String(act.date).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                          const dist = act.distance ? `${(act.distance / 1000).toFixed(1)}km` : '';
                          const dur = act.duration ? `${Math.round(act.duration / 60)}min` : '';
                          return (
                            <button
                              key={act.id}
                              onClick={() => {
                                if (isAttached) {
                                  onAttachedActivityIdsChange(attachedActivityIds.filter(x => x !== act.id));
                                } else {
                                  onAttachedActivityIdsChange([...attachedActivityIds, act.id]);
                                }
                              }}
                              className={`flex items-center gap-2 w-full px-3 py-2 text-left transition-colors border-b border-border/50 last:border-0 ${isAttached ? 'bg-feedback-info-bg/30' : 'hover:bg-surface-hover'}`}
                            >
                              <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isAttached ? 'bg-primary-500 border-action-link' : 'border-neutral-500'}`}>
                                {isAttached && <Check className="w-2.5 h-2.5 text-fg-on-action" />}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-fg truncate">{act.name || act.type || 'Activity'}</p>
                                <p className="text-[10px] text-fg-muted">{[date, act.type, dist, dur].filter(Boolean).join(' · ')}</p>
                              </div>
                            </button>
                          );
                        })}
                      {recentActivities.length === 0 && (
                        <p className="text-xs text-fg-subtle px-3 py-4 text-center">No activities loaded</p>
                      )}
                    </div>
                    {attachedActivityIds.length > 0 && (
                      <div className="px-3 py-2 border-t border-border text-[10px] text-action-link">
                        {attachedActivityIds.length} activit{attachedActivityIds.length === 1 ? 'y' : 'ies'} attached
                      </div>
                    )}
                  </div>
                )}

                {activeMenu === 'saved' && (
                  <div className="absolute bottom-full left-0 mb-2 w-72 bg-surface-raised border border-border-strong rounded-xl shadow-xl overflow-hidden z-10">
                    <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                      <span className="text-xs text-fg-secondary font-medium">Saved Questions</span>
                      {inputMessage.trim() && (
                        <button
                          onClick={() => {
                            if (!savedQuestions.includes(inputMessage.trim())) {
                              onSavedQuestionsChange([inputMessage.trim(), ...savedQuestions]);
                            }
                            setActiveMenu(null);
                          }}
                          className="flex items-center gap-1 text-[10px] text-action-link hover:text-feedback-info-text"
                          title="Save current input as a question"
                        >
                          <Plus className="w-3 h-3" /> Save current
                        </button>
                      )}
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {savedQuestions.length === 0 ? (
                        <p className="text-xs text-fg-subtle px-3 py-4 text-center">
                          No saved questions yet.{inputMessage.trim() ? '' : ' Type a question and tap "Save current" to add one.'}
                        </p>
                      ) : (
                        savedQuestions.map((q, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 hover:bg-surface-hover transition-colors border-b border-border/50 last:border-0 group">
                            <button
                              onClick={() => { setInputMessage(q); setActiveMenu(null); }}
                              className="flex-1 text-left text-xs text-fg-secondary hover:text-fg truncate"
                              title={q}
                            >
                              {q}
                            </button>
                            <button
                              onClick={() => onSavedQuestionsChange(savedQuestions.filter((_, j) => j !== i))}
                              className="flex-shrink-0 text-fg-disabled hover:text-feedback-danger-icon opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-1">
                  {quickActions.length > 0 && onQuickAction && (
                    <button
                      onClick={() => setActiveMenu(v => v === 'suggestions' ? null : 'suggestions')}
                      title="Suggestions"
                      className={`${sidebar ? 'w-8 h-8' : 'w-9 h-9'} flex items-center justify-center rounded-xl border transition-colors ${
                        activeMenu === 'suggestions'
                          ? 'bg-action border-border-focus text-fg-on-action'
                          : 'bg-surface-raised border-border-strong text-fg-muted hover:text-action-link hover:border-action'
                      }`}
                    >
                      <Sparkles className={`${sidebar ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                    </button>
                  )}
                  {(!sidebar || expanded) && recentActivities.length > 0 && (
                    <button
                      onClick={() => { setActiveMenu(v => v === 'activities' ? null : 'activities'); setActivityFilter(''); }}
                      title="Attach activities"
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-colors ${
                        activeMenu === 'activities'
                          ? 'bg-action border-border-focus text-fg-on-action'
                          : attachedActivityIds.length > 0
                            ? 'bg-feedback-info-bg/50 border-feedback-info-border text-action-link'
                            : 'bg-surface-raised border-border-strong text-fg-muted hover:text-action-link hover:border-action'
                      }`}
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>
                  )}
                  {(!sidebar || expanded) && (
                    <button
                      onClick={() => setActiveMenu(v => v === 'saved' ? null : 'saved')}
                      title="Saved questions"
                      className={`w-9 h-9 flex items-center justify-center rounded-xl border transition-colors ${
                        activeMenu === 'saved'
                          ? 'bg-action border-border-focus text-fg-on-action'
                          : 'bg-surface-raised border-border-strong text-fg-muted hover:text-action-link hover:border-action'
                      }`}
                    >
                      <Bookmark className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-end gap-2 flex-1 min-w-0">
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => { setInputMessage(e.target.value); autoResize(); }}
                  onKeyPress={handleKeyPress}
                  placeholder={sidebar ? "Ask your coach..." : "Ask about training, race strategy, nutrition..."}
                  className={`flex-1 min-w-0 bg-surface-raised border border-border-strong text-fg placeholder:text-fg-subtle rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-transparent ${sidebar ? 'text-[13px]' : 'text-[15px]'} transition-shadow overflow-y-auto`}
                  rows={sidebar ? 1 : 2}
                  cols={1}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() && attachedActivityIds.length === 0}
                  className={`${sidebar ? 'w-8 h-8' : 'w-11 h-11'} bg-gradient-to-br from-primary-500 to-action hover:from-action hover:to-action-hover disabled:from-neutral-200 disabled:to-neutral-200 text-fg-on-action disabled:text-fg-muted rounded-xl flex-shrink-0 transition-all flex items-center justify-center shadow-sm`}
                >
                  <Send className={`${sidebar ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Floating chat toggle button — shown on pages without the sidebar.
 */
export const ChatToggleButton = ({ onClick, hasUnread }) => (
  <button
    onClick={onClick}
    className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-gradient-to-br from-primary-500 to-action hover:from-action hover:to-action-hover text-fg-on-action rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
    title="Open AI Coach"
  >
    <MessageCircle className="w-6 h-6" />
    {hasUnread && (
      <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-feedback-danger-solid rounded-full border-2 border-white" />
    )}
  </button>
);

/**
 * Floating chat overlay — a fixed panel that slides up from the bottom-right.
 * Owns historyView state and injects it into its ChatPanel child.
 */
export const ChatOverlay = ({ children, onClose, messages = [], startNewConversation, sessionHistory = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const [historyView, setHistoryView] = useState(null);

  // Inject controlled history props and overlay expand state into the ChatPanel child
  const childWithHistory = React.cloneElement(children, {
    historyView,
    onHistoryViewChange: setHistoryView,
    expanded,
  });

  return (
    <div className={`fixed z-50 bg-surface-subtle shadow-2xl border border-border flex flex-col overflow-hidden transition-all duration-200 ${
      expanded
        ? 'inset-4 sm:inset-8 rounded-2xl'
        : 'bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-[380px] h-[70vh] sm:h-[560px] max-h-[80vh] sm:rounded-2xl rounded-t-2xl'
    }`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-surface-raised flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-action-link" />
          <span className="text-sm font-medium text-fg">
            {historyView ? 'History' : 'AI Coach'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {sessionHistory.length > 0 && (
            <button
              onClick={() => setHistoryView(v => v ? null : 'list')}
              className={`p-1 rounded transition-colors ${historyView ? 'text-action-link' : 'text-fg-muted hover:text-fg'}`}
              title="Conversation history"
            >
              <Clock className="w-4 h-4" />
            </button>
          )}
          {messages.length > 1 && startNewConversation && !historyView && (
            <button onClick={startNewConversation} className="text-xs text-fg-muted hover:text-fg px-2 py-1 rounded transition-colors" title="New conversation">
              New
            </button>
          )}
          <button onClick={() => setExpanded(v => !v)} className="p-1 text-fg-muted hover:text-fg rounded transition-colors" title={expanded ? 'Shrink chat' : 'Expand chat'}>
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button onClick={onClose} className="p-1 text-fg-muted hover:text-fg rounded transition-colors" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {childWithHistory}
    </div>
  );
};

export default ChatPanel;
